import styled, { css } from "styled-components";
import { ChevronRight } from "lucide-react";
import { getNewLookTokens } from "common/NewLook/tokens.ts";
import { orderCard } from "features/trades/NewLook/drawerDetails.ts";
import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import { MOBILE_SEARCH } from "common/constants";
import EditOrderActions from "features/trades/EditTrade/EditOrderActions";
import PreviousValuePopover from "features/trades/EditTrade/PreviosValuePopover";
import ExpandingCard from "common/Boxes/ExpandingCard";
import OrderCardExtraData from "features/trades/TradeDrawer/Orders/OrderCardExtraInfo";
import CaretRightOutlined from "@ant-design/icons/CaretRightOutlined";
import useMedia from "utils/hooks/useMedia";
import Col from "antd/es/col";
import TextWithPrecision from "common/Text/TextWithPrecision";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import { ICON_TYPES } from "@shared/common/constants";
import { OrderTypes } from "@shared/features/trades/common/types";
import {
  GetTradeExtraInfoOrdersResponse,
  LanguageNameSpaces,
  OrderStatus,
  PrecisionType,
} from "@shared/types";
import { EditOrderData } from "@shared/features/trades/EditTrade/types";
import Link from "common/Typography/Link";
import useIntercomArticle from "utils/hooks/useIntercomArticle";
import OrderNumber from "common/Numbering/OrderNumber";
import useOrderCard from "@shared/features/trades/EditTrade/useOrderCard";
import OrderCardStatus from "features/trades/TradeDrawer/Orders/OrderCardStatus.tsx";
import React, { CSSProperties, useCallback } from "react";
import { typography } from "@shared/common/Themes/typography.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface OrderCardProps {
  responseData?: GetTradeExtraInfoOrdersResponse;
  hideAmounts: boolean;
  index: number;
  order: EditOrderData;
  isEdit?: boolean;
  orderType: OrderTypes;
  columnSpans: number[];
  expanded: boolean;
  setExpanded: (index: number) => void;
}

const OrderCard = ({
  order,
  index,
  hideAmounts,
  isEdit,
  orderType,
  columnSpans,
  expanded,
  setExpanded: propsSetExpanded,
}: OrderCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { openIntercomArticleInChat } = useIntercomArticle();
  const {
    appTheme,
    precisionConfig,
    amountChanged,
    quantityChanged,
    priceChanged,
    ratioChanged,
    orderExtraData,
    showExtraData,
    priceCoin,
    amountCoin,
    previousRatio,
    currentRatio,
  } = useOrderCard({ order, isEdit });

  const additionalProps = {
    iconStyle: { position: "absolute" } as CSSProperties,
    textStyle: { position: "relative", left: 20 } as CSSProperties,
  };

  const popupsContainerId = `orderCard-${order.id}`;
  const amountDisplay = (
    <PreviousValuePopover
      popupsContainerId={popupsContainerId}
      valueName={t("orders.amount")}
      valueChanged={amountChanged}
      previousValue={
        <TextWithPrecisionAndIcon
          text={order.amount}
          precisionConfig={precisionConfig}
          precisionType={PrecisionType.AMOUNT}
          precisionCoin={amountCoin}
          iconType={ICON_TYPES.COIN}
          coinOrExchangeName={amountCoin}
          withFallback={true}
        />
      }
      value={
        <TextWithPrecisionAndIcon
          text={order.edit_amount}
          precisionConfig={precisionConfig}
          precisionType={PrecisionType.AMOUNT}
          precisionCoin={amountCoin}
          iconType={ICON_TYPES.COIN}
          coinOrExchangeName={amountCoin}
          withFallback={true}
          {...additionalProps}
        />
      }
    />
  );
  const quantityDisplay = (
    <PreviousValuePopover
      popupsContainerId={popupsContainerId}
      valueName={t("orders.quantity")}
      valueChanged={quantityChanged}
      previousValue={
        <TextWithPrecision
          text={order.quantity}
          precisionConfig={precisionConfig}
          precisionType={PrecisionType.QUANTITY}
        />
      }
      value={
        <TextWithPrecision
          text={order.edit_quantity}
          precisionConfig={precisionConfig}
          precisionType={PrecisionType.QUANTITY}
        />
      }
      alignBottom={true}
      mediumWeight={true}
      color={appTheme.colors.secondaryText}
    />
  );
  const priceDisplay = (
    <PreviousValuePopover
      popupsContainerId={popupsContainerId}
      valueName={t("orders.price")}
      valueChanged={priceChanged}
      previousValue={
        <TextWithPrecisionAndIcon
          text={order.target_price}
          precisionConfig={precisionConfig}
          precisionType={PrecisionType.PRICE}
          precisionCoin={priceCoin}
          iconType={ICON_TYPES.COIN}
          coinOrExchangeName={priceCoin}
        />
      }
      value={
        <TextWithPrecisionAndIcon
          text={order.edit_target_price}
          precisionConfig={precisionConfig}
          precisionType={PrecisionType.PRICE}
          precisionCoin={priceCoin}
          iconType={ICON_TYPES.COIN}
          coinOrExchangeName={priceCoin}
          {...additionalProps}
        />
      }
      alignBottom={true}
      mediumWeight={true}
      color={appTheme.colors.secondaryText}
    />
  );
  const ratioDisplay = (
    <PreviousValuePopover
      valueName={t("orders.ratio")}
      popupsContainerId={popupsContainerId}
      valueChanged={ratioChanged}
      previousValue={`${previousRatio}%`}
      value={`${currentRatio}%`}
    />
  );

  let cardExtraData = showExtraData ? (
    <OrderCardExtraData orderExtraData={orderExtraData} />
  ) : undefined;

  const orderNumber = (
    <OrderNumber
      displayNumber={index + 1}
      size="l"
      style={isMobile ? { position: "absolute", left: 0 } : {}}
    />
  );

  const orderReason =
    !isEdit && order.reason_link ? (
      <Link
        onClick={(e: MouseEvent) => {
          e.stopPropagation();
          openIntercomArticleInChat(order.reason_link);
        }}
      >
        {" "}
        ({order.reason})
      </Link>
    ) : (
      <Text type="medium" $size={typography.fontSizes.S}>
        ({order.reason})
      </Text>
    );

  const setExpanded = useCallback(() => propsSetExpanded(index), [index]);

  const editOrderActions = order.status === OrderStatus.PENDING && isEdit && (
    <EditOrderActions orderType={orderType} orderIndex={index} order={order} />
  );

  // New look (desktop): one compact row, with the status on the right
  // instead of a separate centered status row.
  if (appTheme.isNewLook && !isMobile) {
    return (
      <Wrapper
        id={popupsContainerId}
        collapsibleContent={cardExtraData}
        expanded={expanded}
        setExpanded={setExpanded}
      >
        <Flex $alignItems="center">
          <Col span={columnSpans[0]}>
            <Flex $justifyContent="flex-start">
              {orderNumber}
              {ratioDisplay}
            </Flex>
          </Col>
          <Col span={columnSpans[1]}>{priceDisplay}</Col>
          {!hideAmounts && (
            <Col span={columnSpans[2]}>
              {amountDisplay}
              <Text as="div" type="secondary">
                {quantityDisplay}
              </Text>
            </Col>
          )}
          <NewLookStatusCell>
            <OrderCardStatus status={order.status} />
            {order.reason && <NewLookReason>{orderReason}</NewLookReason>}
            {editOrderActions && (
              <NewLookActionsSlot>{editOrderActions}</NewLookActionsSlot>
            )}
            {showExtraData && !hideAmounts && (
              <NewLookExpandIcon
                size={16}
                strokeWidth={2}
                $expanded={expanded}
              />
            )}
          </NewLookStatusCell>
        </Flex>
      </Wrapper>
    );
  }

  return (
    <Wrapper
      id={popupsContainerId}
      collapsibleContent={cardExtraData}
      expanded={expanded}
      setExpanded={setExpanded}
    >
      <StatusWrapper>
        {isMobile && orderNumber}
        <OrderCardStatus status={order.status} />
        {order.reason ? <div style={{ marginLeft: 6 }}>{orderReason}</div> : ""}
        {order.status === OrderStatus.PENDING && isEdit && (
          <EditOrderActions
            orderType={orderType}
            orderIndex={index}
            order={order}
          />
        )}
      </StatusWrapper>
      <Flex>
        <Col span={columnSpans[0]}>
          <Flex $justifyContent="flex-start">
            {!isMobile && orderNumber}
            {ratioDisplay}
          </Flex>
        </Col>
        <Col span={columnSpans[1]}>{priceDisplay}</Col>
        {!hideAmounts && (
          <Col span={columnSpans[2]} style={{ width: "100%" }}>
            <Flex>
              <div>
                {amountDisplay}
                <Text as="div" type="secondary">
                  {quantityDisplay}
                </Text>
              </div>
              {showExtraData && (
                <div style={{ paddingLeft: isMobile ? 4 : 12 }}>
                  <ExpandCollapseIcon $expanded={expanded} />
                </div>
              )}
            </Flex>
          </Col>
        )}
      </Flex>
    </Wrapper>
  );
};

export default React.memo(OrderCard);

const STATUS_ROW_INSET = 32;

const Wrapper = styled(ExpandingCard)`
  padding: 12px 16px;
  background-color: ${(p) => p.theme.colors.cardBgColor};
  ${(p) =>
    p.theme.isDarkMode &&
    `
  box-shadow: none;
  `};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      ${orderCard};
      &:hover {
        background: ${getNewLookTokens(p.theme).rowHover};
      }
    `}
`;
const NewLookExpandIcon = styled(ChevronRight)<{ $expanded: boolean }>`
  flex: none;
  cursor: pointer;
  color: ${(p) => getNewLookTokens(p.theme).muted};
  transform: rotate(${(p) => (p.$expanded ? 90 : 0)}deg);
  transition: transform 0.25s;
`;
const NewLookStatusCell = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 4px 8px;
`;
const NewLookReason = styled.div`
  min-width: 0;
  text-align: right;
`;
const NewLookActionsSlot = styled.div`
  position: relative;
  flex: none;
  width: 24px;
  height: 24px;
`;
const ExpandCollapseIcon = styled(CaretRightOutlined)<{ $expanded: boolean }>`
  cursor: pointer;
  flex: none;
  color: ${(p) => p.theme.colors.primaryGrey};
  ${(p) =>
    p.$expanded &&
    `
    transform: rotate(90deg);
  `}
  transition: transform 0.35s;
`;
export const StatusWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: ${(p) => p.theme.borders.lightest};
  padding-bottom: 8px;
  margin-bottom: 12px;
  height: 32px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      position: relative;
      flex-wrap: wrap;
      row-gap: 2px;
      height: auto;
      min-height: 32px;
      padding-inline: ${STATUS_ROW_INSET}px;
      border-bottom: 1px solid ${getNewLookTokens(p.theme).hairline};
      margin-bottom: 10px;
      text-align: center;
    `}
`;
